import "./styles/app.css";
import type { MediaConnection, DataConnection } from "./lib/peer";
import { createSession } from "./lib/peer";
import {
  getLocalStream,
  setMic,
  setCam,
  stopStream,
  isPermissionDenied,
} from "./lib/media";
import { render, toast } from "./lib/ui";
import { normalizeCode, isValidCode } from "./lib/words";
import { forceRelay } from "./lib/config";
import {
  isControlMessage,
  RING_TIMEOUT_MS,
  RECONNECT_TIMEOUT_MS,
  type ControlMessage,
} from "./lib/control";
import { monitorQuality, type QualityMonitor } from "./lib/quality";
import { showLobby } from "./screens/lobby";
import { showIncoming } from "./screens/incoming";
import { showInCall, type InCallView } from "./screens/incall";

type Peer = Awaited<ReturnType<typeof createSession>>["peer"];

class App {
  private code = "";
  private peer!: Peer;
  private localStream: MediaStream | null = null;
  private localHasVideo = false;

  private control: DataConnection | null = null; // active control channel
  private media: MediaConnection | null = null;
  private view: InCallView | null = null;
  private qmon: QualityMonitor | null = null;

  private busy = false; // a call session (ringing/incoming/connected) is active
  private pendingOutgoing: string | null = null; // target while we ring it
  private answering: string | null = null; // callee: code we agreed to answer

  private ringTimer?: number;
  private reconnectTimer?: number;

  async start(): Promise<void> {
    render(`<div class="screen lobby"><p class="hint">Connecting…</p></div>`);
    try {
      const session = await createSession();
      this.peer = session.peer;
      this.code = session.code;
    } catch (err) {
      render(
        `<div class="screen lobby"><p class="hint">${String(
          err
        )}<br/>Please reload.</p></div>`
      );
      return;
    }

    this.peer.on("connection", (c) => this.onIncomingControl(c));
    this.peer.on("call", (m) => this.onIncomingMedia(m));
    this.peer.on("disconnected", () => this.peer.reconnect());
    this.peer.on("error", (err) => this.onPeerError(err));

    this.goLobby();
    if (forceRelay) toast("Force-relay (TURN) test mode");
    this.maybeAutoDial();
  }

  // ---------- lobby / routing ----------

  private goLobby(): void {
    showLobby({ myCode: this.code, onCall: (code) => void this.dial(code) });
  }

  private maybeAutoDial(): void {
    const target = normalizeCode(
      new URLSearchParams(location.search).get("call") ?? ""
    );
    if (isValidCode(target) && target !== this.code) {
      history.replaceState(null, "", location.pathname);
      void this.dial(target);
    }
  }

  // ---------- outgoing ----------

  private async dial(target: string): Promise<void> {
    if (this.busy) return;
    const stream = await this.acquireMedia();
    if (!stream) return;

    this.busy = true;
    this.pendingOutgoing = target;

    const control = this.peer.connect(target, { reliable: true });
    this.control = control;
    this.enterCall(target, "Calling…");

    control.on("open", () => {
      this.send({ type: "ring" });
      this.view?.setStatus("Ringing…");
      this.ringTimer = window.setTimeout(() => {
        this.send({ type: "cancel" });
        toast("No answer");
        this.endCall();
      }, RING_TIMEOUT_MS);
    });
    control.on("data", (d) => this.onControlData(d, target));
    control.on("close", () => {
      if (!this.media) this.endCall(); // hung up before media established
    });
  }

  private onControlData(data: unknown, target: string): void {
    if (!isControlMessage(data)) return;
    switch (data.type) {
      case "accept": {
        window.clearTimeout(this.ringTimer);
        if (!this.localStream) return;
        const media = this.peer.call(target, this.localStream);
        this.media = media;
        this.view?.setStatus("Connecting…");
        this.wireMedia(media);
        break;
      }
      case "decline":
        toast("Call declined");
        this.endCall();
        break;
      case "busy":
        toast("User is busy");
        this.endCall();
        break;
      case "hangup":
        this.endCall();
        break;
      default:
        break; // ring / cancel: nothing to do as caller
    }
  }

  // ---------- incoming ----------

  private onIncomingControl(conn: DataConnection): void {
    conn.on("data", (data) => {
      if (!isControlMessage(data)) return;

      if (data.type === "ring") {
        if (this.busy) {
          this.replyBusy(conn);
          return;
        }
        this.busy = true;
        this.control = conn;
        this.pendingOutgoing = null;
        this.showIncomingUI(conn.peer);
      } else if (data.type === "hangup" || data.type === "cancel") {
        // Caller gave up before we answered, or ended an active call.
        if (this.control === conn) this.endCall();
      }
    });
    conn.on("close", () => {
      if (this.control === conn && !this.media) this.endCall();
    });
  }

  private showIncomingUI(caller: string): void {
    showIncoming({
      callerCode: caller,
      onAccept: async () => {
        const stream = await this.acquireMedia();
        if (!stream) {
          this.send({ type: "decline" });
          this.endCall();
          return;
        }
        this.answering = caller;
        this.send({ type: "accept" });
        this.enterCall(caller, "Connecting…");
        // The caller now initiates the media call -> onIncomingMedia answers.
      },
      onDecline: () => {
        this.send({ type: "decline" });
        this.endCall();
      },
    });
  }

  private onIncomingMedia(media: MediaConnection): void {
    if (this.answering && media.peer === this.answering && this.localStream) {
      this.media = media;
      media.answer(this.localStream);
      this.wireMedia(media);
    } else {
      media.close(); // unsolicited / out-of-band call
    }
  }

  // ---------- shared media wiring ----------

  private wireMedia(media: MediaConnection): void {
    media.on("stream", (remote) => {
      this.view?.setRemoteStream(remote);
      this.view?.setStatus("Connected");
      this.attachStats(media);
    });
    media.on("close", () => {
      toast("Call ended");
      this.endCall();
    });
    media.on("error", () => {
      toast("Call error");
      this.endCall();
    });
  }

  private attachStats(media: MediaConnection): void {
    const pc = media.peerConnection;
    if (!pc) return;

    this.qmon = monitorQuality(pc, (q) => this.view?.setQuality(q));

    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      if (s === "disconnected") {
        this.view?.setStatus("Reconnecting…");
        this.view?.setQuality(null);
        window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = window.setTimeout(() => {
          if (pc.iceConnectionState === "disconnected") {
            toast("Connection lost");
            this.endCall();
          }
        }, RECONNECT_TIMEOUT_MS);
      } else if (s === "connected" || s === "completed") {
        window.clearTimeout(this.reconnectTimer);
        this.view?.setStatus("Connected");
      } else if (s === "failed") {
        window.clearTimeout(this.reconnectTimer);
        toast("Connection failed");
        this.endCall();
      }
    };
  }

  // ---------- helpers ----------

  private enterCall(peerCode: string, status: string): void {
    this.view = showInCall({
      peerCode,
      localStream: this.localStream!,
      hasLocalVideo: this.localHasVideo,
      onToggleMic: (on) => setMic(this.localStream!, on),
      onToggleCam: (on) => setCam(this.localStream!, on),
      onHangup: () => {
        this.send({ type: "hangup" });
        this.endCall();
      },
    });
    this.view.setStatus(status);
  }

  private send(msg: ControlMessage): void {
    if (this.control?.open) {
      try {
        this.control.send(msg);
      } catch {
        /* channel already closing */
      }
    }
  }

  private replyBusy(conn: DataConnection): void {
    const fire = () => {
      try {
        conn.send({ type: "busy" } satisfies ControlMessage);
      } catch {
        /* ignore */
      }
      window.setTimeout(() => conn.close(), 200);
    };
    if (conn.open) fire();
    else conn.on("open", fire);
  }

  private async acquireMedia(): Promise<MediaStream | null> {
    if (this.localStream) return this.localStream;
    try {
      const media = await getLocalStream();
      this.localStream = media.stream;
      this.localHasVideo = media.hasVideo;
      if (!media.hasVideo) toast("No camera found — joining with audio only");
      return this.localStream;
    } catch (e) {
      if (isPermissionDenied(e)) toast("Allow camera & mic to call");
      else if ((e as DOMException).name === "NotFoundError")
        toast("No camera or microphone found");
      else toast("Could not access camera/microphone");
      return null;
    }
  }

  private endCall(): void {
    if (!this.busy) return; // already torn down — keep this idempotent
    this.busy = false;

    window.clearTimeout(this.ringTimer);
    window.clearTimeout(this.reconnectTimer);
    this.qmon?.stop();
    this.qmon = null;

    try {
      this.media?.close();
    } catch {
      /* ignore */
    }
    try {
      this.control?.close();
    } catch {
      /* ignore */
    }
    this.media = null;
    this.control = null;
    this.view = null;
    this.pendingOutgoing = null;
    this.answering = null;

    stopStream(this.localStream);
    this.localStream = null;
    this.localHasVideo = false;

    this.goLobby();
  }

  private onPeerError(err: { type?: string }): void {
    if (err.type === "peer-unavailable") {
      if (this.pendingOutgoing) {
        toast("User is offline");
        this.endCall();
      }
      return;
    }
    console.warn("peer error:", err);
  }
}

new App().start();
